import {useState, useEffect, useRef} from 'react';

export function useBookmarkSessionTracking(storageKey: string, feedData: any) {
  // Load initialBookmarkedIds from sessionStorage if available (persists across navigation)
  const [initialBookmarkedIds, setInitialBookmarkedIds] = useState<Set<string>>(() => {
    try {
      const stored = sessionStorage.getItem(storageKey);
      return stored ? new Set(JSON.parse(stored)) : new Set();
    } catch {
      return new Set();
    }
  });
  const [sessionBookmarkedIds, setSessionBookmarkedIds] = useState<Set<string>>(new Set());

  // Check if we've already initialized from sessionStorage - use lazy init
  const hasInitializedRef = useRef<boolean | null>(null);
  if (hasInitializedRef.current === null) {
    try {
      hasInitializedRef.current = sessionStorage.getItem(storageKey) !== null;
    } catch {
      hasInitializedRef.current = false;
    }
  }

  // Populate `initialBookmarkedIds` from the first feed response only. Discover sections
  // exclude bookmarked items, so initial bookmarks only live in `bookmarked_upcoming` — that
  // section must be scanned to populate the set correctly.
  //
  // We intentionally do NOT re-derive `sessionBookmarkedIds` from feed state on refetch:
  // session tracking is driven by `handleBookmark` (in useBookmarkActions) so we avoid
  // double-counting when the cache's bookmarked state disagrees with a stale initial set
  // (e.g. a sessionStorage entry written before a prior bugfix).
  useEffect(() => {
    if (hasInitializedRef.current) return;
    if (!feedData?.data?.data) return;

    const data = feedData.data.data;
    const bookmarked = new Set<string>();
    [
      ...(data.hyper_local || []),
      ...(data.system_popular || []),
      ...(data.general_upcoming || []),
      ...(data.bookmarked_upcoming || []),
    ].forEach((article: any) => {
      if (article.is_bookmarked) {
        bookmarked.add(article.id.toString());
      }
    });

    hasInitializedRef.current = true;
    setInitialBookmarkedIds(new Set(bookmarked));
    try {
      sessionStorage.setItem(storageKey, JSON.stringify(Array.from(bookmarked)));
    } catch {}
  }, [feedData]);

  return {
    initialBookmarkedIds,
    sessionBookmarkedIds,
    setSessionBookmarkedIds,
  };
}
